import { EmailRowData, SourceIcons, BrevoIcons, getStatusBadge } from '@/lib/email-formatters';
import { formatRelativeDate } from '@/lib/format-relative-date';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Database,
  ArrowUpDown,
  Megaphone,
  Zap,
} from 'lucide-react';

interface EmailTableProps {
  emails: EmailRowData[];
  loading: boolean;
  page: number;
  pages: number;
  total: number;
  onPageChange: (page: number) => void;
  sort?: string;
  order?: string;
  onSort?: (field: string) => void;
  onRowClick: (row: EmailRowData) => void;
  timezone?: string;
}

export default function EmailTable({
  emails,
  loading,
  page,
  pages,
  onPageChange,
  sort,
  onSort,
  onRowClick,
  timezone = 'UTC',
}: EmailTableProps) {
  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => {
    if (!onSort) return <TableHead>{children}</TableHead>;
    return (
      <TableHead
        className="cursor-pointer select-none hover:text-foreground"
        onClick={() => onSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {sort === field && (
            <ArrowUpDown className="h-3 w-3" />
          )}
        </div>
      </TableHead>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Database className="h-10 w-10 mb-3 opacity-50" />
        <p>No emails found</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <SortHeader field="email">Email</SortHeader>
            <SortHeader field="status">Status</SortHeader>
            <TableHead>Blocklist</TableHead>
            <TableHead>Delivery</TableHead>
            <TableHead>Projects</TableHead>
            <SortHeader field="verified_at">Last Activity</SortHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {emails.map((row) => (
            <TableRow
              key={row.email}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onRowClick(row)}
            >
              <TableCell className="font-mono text-sm">{row.email}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  {getStatusBadge(row.status)}
                  <SourceIcons row={row} />
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Megaphone className={`h-3.5 w-3.5 ${row.marketing_blocked ? 'text-red-500' : 'text-gray-200'}`} />
                  <Zap className={`h-3.5 w-3.5 ${row.transactional_blocked ? 'text-red-500' : 'text-gray-200'}`} />
                </div>
              </TableCell>
              <TableCell><BrevoIcons row={row} /></TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {row.projects.map((p) => (
                    <Badge key={p} variant="outline" className="text-xs font-normal">
                      {p}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                {formatRelativeDate(row.last_activity, timezone)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {pages > 1 && (
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground">
            Page {page} of {pages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
